import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Gamepad2, ChevronRight } from "lucide-react";

/**
 * XboxLookupDialog - Dialog for selecting Xbox titles from search results
 */
export function XboxLookupDialog({
  open,
  onOpenChange,
  results,
  onSelectTitle,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-green-400" />
            Select Xbox Title
          </DialogTitle>
          <DialogDescription>
            Found {results?.length || 0} matching titles in your Xbox history
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {!results || results.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <Gamepad2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No matching titles found</p>
              <p className="text-sm mt-2">Make sure you've played this game on your Xbox account</p>
            </div>
          ) : (
            results.map((title) => (
              <button
                key={title.titleId}
                onClick={() => onSelectTitle(title)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                  title.isExactMatch 
                    ? 'border-green-500/50 bg-green-500/10 hover:bg-green-500/20' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`xbox-title-${title.titleId}`}
              >
                {title.displayImage ? (
                  <img 
                    src={title.displayImage} 
                    alt={title.name}
                    className="w-12 h-12 rounded object-contain bg-black/50"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2 flex-wrap">
                    <span className="truncate">{title.name}</span>
                    {title.isExactMatch && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-300 border border-green-500/30 shrink-0">EXACT</span>
                    )}
                    {title.source === "master_db" ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 shrink-0" title="From Master Database">
                        MDB
                      </span>
                    ) : title.source === "played" ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30 shrink-0" title="From your Xbox play history">
                        PLAYED
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-white/60 flex items-center gap-3">
                    <span>{title.platformType}</span>
                    {(title.totalAchievements > 0 || title.totalGamerscore > 0) && (
                      <>
                        <span>•</span>
                        <span>{title.totalAchievements} achievements</span>
                        <span>•</span>
                        <span>{title.totalGamerscore}G</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">
                    Title ID: {title.titleId}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/40" />
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SteamLookupDialog - Dialog for selecting Steam games from search results
 */
export function SteamLookupDialog({
  open,
  onOpenChange,
  results,
  onSelectGame,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-blue-400" />
            Select Steam Game
          </DialogTitle>
          <DialogDescription>
            Found {results?.length || 0} matching Steam games
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {!results || results.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <Gamepad2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No matching Steam games found</p>
              <p className="text-sm mt-2">Try a different search term</p>
            </div>
          ) : (
            results.map((game) => (
              <button
                key={game.appId}
                onClick={() => onSelectGame(game)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                  game.isExactMatch 
                    ? 'border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`steam-game-${game.appId}`}
              >
                {game.displayImage ? (
                  <img 
                    src={game.displayImage} 
                    alt={game.name}
                    className="w-12 h-16 rounded object-contain bg-black/50"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-16 rounded bg-white/10 flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {game.name}
                    {game.isExactMatch && (
                      <span className="ml-2 text-xs text-blue-400">(exact match)</span>
                    )}
                  </div>
                  <div className="text-xs text-white/60 flex items-center gap-3">
                    {game.owned ? (
                      <span className="text-green-400">Owned</span>
                    ) : (
                      <span>Not owned</span>
                    )}
                    {game.playtimeMinutes > 0 && (
                      <>
                        <span>•</span>
                        <span>{Math.round(game.playtimeMinutes / 60)}h played</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">
                    App ID: {game.appId}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/40" />
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PSNLookupDialog - Dialog for selecting PlayStation games from search results
 */
export function PSNLookupDialog({
  open,
  onOpenChange,
  results,
  onSelectGame,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-blue-500" />
            Select PlayStation Game
          </DialogTitle>
          <DialogDescription>
            Found {results?.length || 0} matching games in your trophy list
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {!results || results.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <Gamepad2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No matching PlayStation games found</p>
              <p className="text-sm mt-2">Make sure you've played this game on your PSN account</p>
            </div>
          ) : (
            results.map((game) => (
              <button
                key={game.npCommunicationId}
                onClick={() => onSelectGame(game)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                  game.isExactMatch 
                    ? 'border-blue-600/50 bg-blue-600/10 hover:bg-blue-600/20' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`psn-game-${game.npCommunicationId}`}
              >
                {game.displayImage ? (
                  <img 
                    src={game.displayImage} 
                    alt={game.name}
                    className="w-12 h-12 rounded object-contain bg-black/50"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {game.name}
                    {game.isExactMatch && (
                      <span className="ml-2 text-xs text-blue-500">(exact match)</span>
                    )}
                  </div>
                  <div className="text-xs text-white/60 flex items-center gap-3">
                    <span>{game.platformType}</span>
                    <span>•</span>
                    <span>{game.totalTrophies} trophies</span>
                    {game.hasPlatinum && (
                      <>
                        <span>•</span>
                        <span className="text-yellow-400">🏆 Platinum</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">
                    NP ID: {game.npCommunicationId}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/40" />
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
